import React, { useState } from "react";
import {
  TouchableOpacity,
  StyleSheet,
  TextInput,
  View,
  Modal,
  Text,
  Alert,
  ScrollView,
} from "react-native";
import { Badge, Button } from "react-native-paper";
import { renameLabel } from "../../../utils/common";
import { getAllFilteredOperations } from "../../../services/operationService";

interface Props {
  itemsList: Array<string>;
  filterCategory: string;
  setFilterCategory: (category: string) => void;
  tableHeaders: any; // Add tableHeaders prop if it's not global
}

export const OperationBadgeHorizontalList: React.FC<Props> = ({
  itemsList,
  filterCategory,
  setFilterCategory,
  tableHeaders,
}) => {
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleDateChange = (key: "from" | "to", value: string) => {
    setDateRange((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilter = (filter: string) => {
    setFilterCategory(filter);
  };

  const validateAndApply = () => {
    if (!dateRange.from || !dateRange.to) {
      setErrorMessage("Both 'From' and 'To' dates are required.");
      return;
    }

    handleCustomDateApply(dateRange);
    setIsModalVisible(false);
  };

  const handleCustomDateApply = (dateRange: any) => {
    if (dateRange.from && dateRange.to) {
      const customFilter = `From: ${dateRange.from} To: ${dateRange.to}`;
      applyFilter(customFilter);
    }
    setIsModalVisible(false);
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};
    filters = {
      dateRange: dateRange,
    };
    getAllFilteredOperations(tableHeaders, filters);
  };

  const handleBadgeClick = (item: string) => {
    const today = new Date();
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};

    if (item === "Custom Date Range") {
      setIsModalVisible(true); // Open modal for custom date selection
      return;
    } else if (item === "Last 3 months") {
      const threeMonthsAgo = new Date(
        today.getFullYear(),
        today.getMonth() - 3,
        today.getDate()
      );
      setDateRange({
        from: threeMonthsAgo.toISOString().split("T")[0],
        to: today.toISOString().split("T")[0],
      });
      filters = {
        dateRange: {
          from: threeMonthsAgo.toISOString().split("T")[0],
          to: today.toISOString().split("T")[0],
        },
      };
    } else {
      filters = { status: item };
    }

    applyFilter(item); // Update the selected badge visually
    getAllFilteredOperations(tableHeaders, filters); // Pass filters for API call
  };

  return (
    <View style={styles.container}>
      {/* Badge Row */}
      <ScrollView horizontal={true} showsHorizontalScrollIndicator={false}>
        <View style={styles.badgesRow}>
          {itemsList.map((item) => {
            const isActive = filterCategory === item;
            return (
              <TouchableOpacity
                key={item}
                style={[
                  styles.filterButton,
                  isActive && styles.activeFilterButton,
                ]}
                onPress={() => handleBadgeClick(item)}
              >
                <Text
                  style={
                    isActive
                      ? styles.activeFilterText
                      : styles.filterText
                  }
                >
                  {renameLabel(item)}
                </Text>
              </TouchableOpacity>
            )
          })}

          {/* Custom Date Range Badge */}
          <TouchableOpacity
            style={[
              styles.filterButton,
              filterCategory.includes("From") && styles.activeFilterButton,
            ]}
            onPress={() => handleBadgeClick("Custom Date Range")}
          >
            <Text
              style={
                filterCategory.includes("From")
                  ? styles.activeFilterText
                  : styles.filterText
              }
            >
              {filterCategory.includes("From")
                ? filterCategory
                : "Custom Date Range"}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Custom Date Range Modal */}
      <Modal
        visible={isModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Custom Date Range</Text>

            {/* From Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select From Date"
              value={dateRange.from}
              onChangeText={(value) => handleDateChange("from", value)}
              onFocus={(e) => (e.currentTarget.type = "date")} // Show date picker on focus
            />

            {/* To Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select To Date"
              value={dateRange.to}
              onChangeText={(value) => handleDateChange("to", value)}
              onFocus={(e) => (e.currentTarget.type = "date")} // Show date picker on focus
            />

            {/* Error Message */}
            {errorMessage ? (
              <Text style={styles.errorMessage}>{errorMessage}</Text>
            ) : null}

            {/* Modal Buttons */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setIsModalVisible(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.applyButton}
                onPress={validateAndApply}
              >
                <Text style={styles.applyButtonText}>Apply</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 10,
  },
  badgesRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  activeFilterButton: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500",
  },
  activeFilterText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    width: "92%",
    maxWidth: 400,
    borderRadius: 12,
    padding: 24,
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 19,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 20,
    textAlign: "center",
  },
  dateInput: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
    fontSize: 15,
    color: "#111827",
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginTop: 8,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  cancelButtonText: {
    color: "#374151",
    fontSize: 15,
    fontWeight: "600",
  },
  applyButton: {
    flex: 1,
    backgroundColor: "#2563EB",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  applyButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
  },
  errorMessage: {
    color: "red",
    marginBottom: 10,
    textAlign: "center",
  },
});
